import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AiOutlineUserAdd,
  AiOutlineSearch,
  AiOutlineExclamationCircle
} from "react-icons/ai";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import CustomerFilterService from "../../services/CustomerFilterService";
import AddCustomerFilterModal from "../../modals/AddCustomerFilter/AddCustomerFilter";
import EditCustomerFilterModal from "../../modals/EditCustomerFilter/EditCustomerFilter";
import CustomerFilterDetailsModal from "../../modals/CustomerFilterDetailsModal/CustomerFilterDetailsModal";
import PrintableTable from "../../components/PrintableTable/PrintableTable";
import Loader from "../../components/Loader/Loader";

import "./CustomersFilters.css";
import "../../styles/action-icons.css";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseStatusFilter = (search) => {
  const params = new URLSearchParams(search);
  const status = params.get("status");
  return status === "overdue" ? "overdue" : null;
};

const isOverdue = (dateString) => {
  if (!dateString) return false;
  const dueDate = new Date(dateString);
  if (Number.isNaN(dueDate.getTime())) return false;
  const today = new Date();
  dueDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

const getStatusInfo = (dateString) => {
  if (!dateString) {
    return { label: "Unknown", tone: "unknown", detail: "No due date" };
  }

  const dueDate = new Date(dateString);
  if (Number.isNaN(dueDate.getTime())) {
    return { label: "Unknown", tone: "unknown", detail: "Invalid date" };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);

  const diffMs = dueDate.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (dueDate < today) {
    const daysOverdue = Math.abs(diffDays);
    return {
      label: "Overdue",
      tone: "overdue",
      detail: `${daysOverdue} ${daysOverdue === 1 ? "day" : "days"} overdue`,
    };
  }

  if (diffDays === 0) {
    return { label: "Due Today", tone: "due-today", detail: "Due today" };
  }

  const soonThreshold = 7;
  if (diffDays <= soonThreshold) {
    return {
      label: "Due Soon",
      tone: "due-soon",
      detail: `${diffDays} ${diffDays === 1 ? "day" : "days"} left`,
    };
  }

  return {
    label: "Active",
    tone: "active",
    detail: `${diffDays} ${diffDays === 1 ? "day" : "days"} left`,
  };
};

const CustomersFilters = () => {
  const [customerFilters, setCustomerFilters] = useState([]);
  const [filteredCustomerFilters, setFilteredCustomerFilters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilterIds, setSelectedFilterIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState(() => parseStatusFilter(location.search));
  const [sortConfig, setSortConfig] = useState({ key: 'toBeChanged', direction: 'asc' });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState({});
  const [selectedFilterForDetails, setSelectedFilterForDetails] = useState({});

  // Load customer filters
  const loadCustomerFilters = async () => {
    setLoading(true);
    try {
      const res = await CustomerFilterService.getAll();
      const list = res.data || [];
      setCustomerFilters(list);
      setFilteredCustomerFilters(list);
    } catch {
      toast.error("Failed to load customer filters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomerFilters();
  }, []);

  useEffect(() => {
    setStatusFilter(parseStatusFilter(location.search));
  }, [location.search]);

  // Filter logic
  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredCustomerFilters(
      customerFilters.filter(row => {
        const matchesSearch =
          String(row.id).includes(lower) ||
          (row.Customer?.firstName || "").toLowerCase().includes(lower) ||
          (row.Customer?.lastName || "").toLowerCase().includes(lower) ||
          (row.Filter?.filter_name || "").toLowerCase().includes(lower) ||
          String(row.date_of_installation).includes(lower) ||
          String(row.date_to_be_changed).includes(lower);

        const matchesStatus = (() => {
          if (!statusFilter) return true;
          if (statusFilter === "overdue") {
            return isOverdue(row.date_to_be_changed);
          }
          return true;
        })();

        return matchesSearch && matchesStatus;
      })
    );
  }, [searchTerm, customerFilters, statusFilter]);

  // Handlers
  const editClickHandler = (e, row) => {
    e.stopPropagation();
    setSelectedFilter(row || {});
    setShowEditModal(true);
  };

  const deleteClickHandler = (e, id) => {
    e.stopPropagation();
    setSelectedFilterId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await CustomerFilterService.remove(selectedFilterId);
      toast.success("Deleted successfully", { autoClose: 2000 });
      await loadCustomerFilters();
    } catch {
      toast.error("Delete failed");
    } finally {
      setShowDeleteModal(false);
      setSelectedFilterId(null);
    }
  };

  // Checkbox handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedFilterIds(filteredCustomerFilters.map(row => row.id));
    } else {
      setSelectedFilterIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedFilterIds(prev =>
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedFilterIds.length === 0) return;
    if (!window.confirm("Are you sure you want to delete the selected customer filters?")) return;
    try {
      await Promise.all(selectedFilterIds.map(id => CustomerFilterService.remove(id)));
      toast.success("Selected customer filters deleted successfully", { autoClose: 2000 });
      setSelectedFilterIds([]);
      await loadCustomerFilters();
    } catch {
      toast.error("Failed to delete selected customer filters");
    }
  };

  // For export/save, pass only selected filters if any, else all
  const handleClearStatusFilter = () => {
    setStatusFilter(null);
    navigate("/customers-filters", { replace: true });
  };

  const getComparableValue = (row, key) => {
    switch (key) {
      case 'id':
        return Number(row.id) || 0;
      case 'customer': {
        const first = row.Customer?.firstName || "";
        const last = row.Customer?.lastName || "";
        return `${first} ${last}`.trim().toLowerCase();
      }
      case 'filter':
        return (row.Filter?.filter_name || "").toLowerCase();
      case 'installed':
        return row.date_of_installation ? new Date(row.date_of_installation).getTime() : -Infinity;
      case 'toBeChanged':
        return row.date_to_be_changed ? new Date(row.date_to_be_changed).getTime() : Infinity;
      case 'status': {
        const info = getStatusInfo(row.date_to_be_changed);
        const order = { overdue: 0, 'due-today': 1, 'due-soon': 2, active: 3, unknown: 4 };
        return order[info.tone] ?? 5;
      }
      default:
        return '';
    }
  };

  const sortedCustomerFilters = useMemo(() => {
    const data = [...filteredCustomerFilters];
    if (!sortConfig?.key) return data;
    data.sort((a, b) => {
      const aVal = getComparableValue(a, sortConfig.key);
      const bVal = getComparableValue(b, sortConfig.key);
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredCustomerFilters, sortConfig]);

  const displayedFilters = selectedFilterIds.length > 0
    ? sortedCustomerFilters.filter(row => selectedFilterIds.includes(row.id))
    : sortedCustomerFilters;

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" aria-hidden="true" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon" aria-hidden="true" />
      : <FaSortDown className="sort-icon" aria-hidden="true" />;
  };

  if (loading) {
    return <Loader message="Loading customer filters..." />;
  }

  return (
    <>
      <div className="customer-filters-list">
        <div className="customer-filters-title">Customer Filters</div>

        <div className="customer-filters-header row">
          <div className="col add-btn-col">
            <button
              className="btn add-filter-button"
              onClick={() => setShowAddModal(true)}
            >
              <AiOutlineUserAdd /> Add Customer Filter
            </button>
          </div>
          <div className="col search-bar-col">
            <div className="search-bar-wrapper">
              <span className="search-icon">
                <AiOutlineSearch />
              </span>
              <input
                type="text"
                placeholder="Search Customer Filters..."
                className="search-bar"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {statusFilter === "overdue" && (
          <div className="status-filter-banner">
            <span>Showing overdue filters only</span>
            <button type="button" onClick={handleClearStatusFilter}>Clear</button>
          </div>
        )}

        {/* Bulk action buttons */}
        {selectedFilterIds.length > 0 && (
          <div className="bulk-actions">
            <button className="btn btn-danger" onClick={handleBulkDelete}>
              <FiTrash2 /> Delete Selected
            </button>
          </div>
        )}

        <div className="table-responsive">
          <PrintableTable
            fileName="customer_filters.pdf"
            title="Customer Filters List"
            showOptions={true}
            data={displayedFilters}
          >
            <table className="table customer-filters-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedFilterIds.length === filteredCustomerFilters.length && filteredCustomerFilters.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('id')}>
                      ID {renderSortIcon('id')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('customer')}>
                      Customer {renderSortIcon('customer')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('filter')}>
                      Filter {renderSortIcon('filter')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('installed')}>
                      Installed {renderSortIcon('installed')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('toBeChanged')}>
                      To Be Changed {renderSortIcon('toBeChanged')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('status')}>
                      Status {renderSortIcon('status')}
                    </button>
                  </th>
                  <th className="no-print">Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedFilters.length > 0 ? (
                  displayedFilters.map(row => {
                    const id = row.id;
                    const customer = row.Customer ? `${row.Customer.firstName} ${row.Customer.lastName}` : "";
                    const filter = row.Filter ? row.Filter.filter_name : "";
                    const installed = formatDate(row.date_of_installation);
                    const toBeChanged = formatDate(row.date_to_be_changed);
                    const statusInfo = getStatusInfo(row.date_to_be_changed);
                    return (
                      <tr key={id} style={{ cursor: "pointer" }} onClick={(e) => {
                        // Don't open modal if clicking on action buttons or checkboxes
                        if (e.target.closest('.no-click') || e.target.type === 'checkbox') {
                          return;
                        }
                        setSelectedFilterForDetails(row);
                        setShowDetailsModal(true);
                      }}>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={selectedFilterIds.includes(id)}
                            onChange={() => handleSelectOne(id)}
                            className="no-click"
                          />
                        </td>
                        <td className="text-center">{id}</td>
                        <td className="text-center">{customer}</td>
                        <td className="text-center">{filter}</td>
                        <td className="text-center">{installed}</td>
                        <td className="text-center">{toBeChanged}</td>
                        <td className="text-center">
                          <span
                            className={`status-pill status-${statusInfo.tone}`}
                            aria-label={`Status: ${statusInfo.label}${statusInfo.detail ? ` – ${statusInfo.detail}` : ""}`}
                          >
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="text-center no-print no-click">
                          <div className="table-action-group">
                            <button
                              type="button"
                              className="table-icon-button edit-button no-click"
                              onClick={e => editClickHandler(e, row)}
                              title="Edit customer filter"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              className="table-icon-button delete-button no-click"
                              onClick={e => deleteClickHandler(e, id)}
                              title="Delete customer filter"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7">No customer filters found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </PrintableTable>
        </div>

        <ToastContainer />

        <AddCustomerFilterModal
          showModal={showAddModal}
          handleClose={() => setShowAddModal(false)}
          onCustomerFilterAdded={loadCustomerFilters}
        />

        <EditCustomerFilterModal
          showModal={showEditModal}
          handleClose={() => {
            setShowEditModal(false);
            setSelectedFilter({});
          }}
          customerFilterData={selectedFilter || {}}
          onCustomerFilterUpdated={loadCustomerFilters}
        />

        <CustomerFilterDetailsModal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          customerFilter={selectedFilterForDetails || {}}
        />

        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          className="delete-confirmation-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-danger)' }}>
              <AiOutlineExclamationCircle style={{ fontSize: '1.6em', color: 'var(--color-danger)' }} />
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="confirmation-text" style={{ fontSize: 'var(--font-size-md)', color: 'var(--color-text)', margin: '1rem 0', textAlign: 'center' }}>
              Are you sure you want to delete this customer filter? This action cannot be undone.
            </div>
          </Modal.Body>
          <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              className="w-100"
              style={{ maxWidth: 120, background: 'var(--color-muted)', color: 'var(--color-text-light)', border: 'none' }}
            >
              No
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              className="w-100"
              style={{ maxWidth: 120, background: 'var(--color-danger)', color: 'var(--color-text-light)', border: 'none' }}
            >
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default CustomersFilters;
